import { Roles } from "@outschool/auth-shared";
import { programsRequireCreditBundle } from "@outschool/funding-programs-shared";
import {
  UpdateCurrentUserDetailsMutation,
  UpdateCurrentUserDetailsMutationVariables
} from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { gql, useMutation } from "@outschool/ui-apollo";
import {
  FullCurrentUserFragment,
  useFundingProgramAbbr,
  useSelfElectedStateFundingProgram,
  useSession
} from "@outschool/ui-auth";
import { useFeatureFlag } from "@outschool/ui-components-shared";
import React from "react";

const updateCurrentUserDetailsMutation = gql`
  mutation UpdateCurrentUserDetails($details: UserDetailsInput) {
    updateCurrentUser(details: $details) {
      ...FullCurrentUserFragment
    }
  }
  ${FullCurrentUserFragment}
`;

let isCurrentUserDetailsUpdated = false;

export function useRequiresMembershipForEnrollment() {
  const { isLoggedIn, currentUser, isEsaSession } = useSession();
  const esaState = useFundingProgramAbbr();
  const { usesCharter } = useSelfElectedStateFundingProgram();

  const enableRequireEsaMembershipForEnrollment = useFeatureFlag(
    "require-esa-membership-for-enrollment"
  );

  // DevWorkbench override for admin exemption
  const [ignoreAdminCheck] = useLocalStorageState(
    "devWorkbench.requiresMembershipForEnrollment.ignoreAdminCheck",
    false
  );

  const [updateCurrentUserDetails] = useMutation<
    UpdateCurrentUserDetailsMutation,
    UpdateCurrentUserDetailsMutationVariables
  >(updateCurrentUserDetailsMutation);

  const currentUserRequiresMembershipForEnrollment =
    !!currentUser?.requiresMembershipForEnrollment;

  // exclude admins from the requirement for now, unless overridden by DevWorkbench
  const isAdmin = !!currentUser?.is_admin;
  const shouldExcludeAdmin = isAdmin && !ignoreAdminCheck;
  const isCharterAdmin =
    currentUser?.admin_for_organization_uid &&
    currentUser.roles?.includes(Roles.CanOrganizationEnroll);

  const requiresMembershipForEnrollment =
    ((!isLoggedIn && !usesCharter()) ||
      currentUserRequiresMembershipForEnrollment ||
      // avoid briefly showing cash for i18n users and required membership states
      currentUser?.requiresMembershipForEnrollment === null) &&
    !isEsaSession &&
    !currentUser?.hasEsaMembership &&
    !shouldExcludeAdmin &&
    !isCharterAdmin;

  React.useEffect(() => {
    if (
      currentUser?.requiresMembershipForEnrollment === null &&
      !shouldExcludeAdmin &&
      !isCurrentUserDetailsUpdated &&
      requiresMembershipForEnrollment
    ) {
      // using a module-level variable to prevent multiple update requests
      isCurrentUserDetailsUpdated = true;
      updateCurrentUserDetails({
        variables: {
          details: {
            requiresMembershipForEnrollment: true,
            // Track phase of the membership rollout we are doing.
            membershipRolloutPhase: "phase_3_final_rollout"
          }
        }
      });
      // no need to clear isCurrentUserDetailsUpdated because we will only ever need to set once
    }
  }, [
    currentUser?.requiresMembershipForEnrollment,
    updateCurrentUserDetails,
    shouldExcludeAdmin,
    requiresMembershipForEnrollment
  ]);

  const requiresEsaMembershipForEnrollment =
    enableRequireEsaMembershipForEnrollment &&
    isLoggedIn &&
    isEsaSession &&
    programsRequireCreditBundle.includes(esaState ?? "") &&
    !currentUser?.hasEsaMembership;

  const mustPurchaseMembership =
    requiresMembershipForEnrollment &&
    (!currentUser?.hasSubscription ||
      !currentUser?.canPayWithSubscriptionCredits);

  return {
    requiresMembershipForEnrollment,
    mustPurchaseMembership,
    requiresEsaMembershipForEnrollment
  };
}
